import { Inject, Injectable } from '@angular/core';
import * as shp from 'shpjs';

import { ILoggingService } from '../services/interfaces/ILoggingService';
import { LayerService } from '../services/layer.service';
import { IMapcoreFeatureProps } from '../types';

@Injectable({ providedIn: 'root' })
export class ImportService {

    public loading?: boolean = null;
    public shpImportObjectIdFieldName: string = 'IMPORT_OBJECTID';

    constructor(
        @Inject('LoggingService') public loggingService: ILoggingService,
        private readonly layerService: LayerService
    ) {}

    public async importFiles(e: DragEvent | Event) {

        // Handle both drag/drop and input type='file' change events
        const files: FileList = (e instanceof DragEvent) ? e.dataTransfer.files : (<HTMLInputElement>e.target).files;
        this.loading = true;
        for (const file of Array.from(files)) {
            if (file.type === 'application/x-zip-compressed') {
                const content: string | ArrayBuffer = await this.readFileAsync(file);
                const geoJson: any = await shp(content);
                const featureSet: IMapcoreFeatureProps[] = this.convertGeoJsonToFeatureSet(geoJson);
                await this.layerService.addFeatureSetLayerToMap(featureSet); // method returns before work is complete.
            }
        }
        this.loading = false;
    }

    private async readFileAsync(file: File): Promise<string | ArrayBuffer | null> {
        return new Promise((resolve, reject) => {
            const fileReader: FileReader = new FileReader();
            fileReader.onload = () => {
                resolve(fileReader.result);
            };
            fileReader.onerror = reject;
            fileReader.readAsArrayBuffer(file);
        });
    }

    private convertGeoJsonToFeatureSet(geoJson: any): IMapcoreFeatureProps[] { // , file: File

        const featureCollections = Array.isArray(geoJson) ? [...geoJson] : [geoJson];
        const featureSets = featureCollections.flatMap((featureCollection) => {
            let shpLayer: IMapcoreFeatureProps;
            if (featureCollection.features && featureCollection.features.length > 0) {
                // const title = featureCollection.fileName.replace(extension, '').replace(/_+/g, ' ');
                shpLayer = {
                    title: featureCollection.fileName.replace(/_+/g, ' '),
                    objectIdField: this.shpImportObjectIdFieldName,
                    outFields: ['*'],
                    visible: true
                };
                shpLayer.fields = this.getFieldsFromGeoJsonAttributes(featureCollection.features[0].properties);
                shpLayer.source = this.mutateGeoJsonFeaturesToSource(featureCollection.features, shpLayer.fields);
            }
            return shpLayer && shpLayer.source.length > 0 ? [shpLayer] : [];
        });
        return featureSets;
    }

    private getFieldsFromGeoJsonAttributes(properties: any): __esri.FieldProperties[] {

        // Detect attribute's JS type and determine ESRI type.
        const js2esri: any = { '[object Number]': 'double', '[object String]': 'string', '[object Date]': 'date' };
        const fields: __esri.FieldProperties[] = Object.entries(properties).map(([key, value]) => ({
            name: key,
            type: js2esri[Object.prototype.toString.call(value)] || 'string'
        }));
        // Add a unique identifier to the field list.
        fields.push({
            name: this.shpImportObjectIdFieldName,
            type: 'oid'
        });
        fields.sort((a, b) => a.type === 'oid' ? 1 : a.name.localeCompare(b.name));
        return fields;
    }

    private mutateGeoJsonFeaturesToSource(features: any, fields: __esri.FieldProperties[]): __esri.GraphicProperties[] {

        // To avoid doubling memory, mutate GeoJSON features into Esri Graphic shape objects
        let i = 0;
        const dateFields = fields.filter(field => field.type === 'date');
        const numberFields = fields.filter(field => field.type === 'double');
        const oidField = fields.filter(field => field.type === 'oid');
        const source: __esri.GraphicProperties[] = features.flatMap(feature => {

            // Convert GeoJSON properties to an ESRI attribute shaped object
            if (feature.properties) {
                feature.attributes = feature.properties;
                delete (feature.properties);
                feature.attributes[oidField[0].name] = i++;
                dateFields.forEach(dateField => {
                    // Avoid ESRI API console conversion warning by giving it UTC epoch format date/time.
                    feature.attributes[dateField.name] = feature.attributes[dateField.name].getTime() || null;
                });
                numberFields.forEach(numberField => {
                    // Avoid ESRI API console conversion warning by changing NaN values to null.
                    if (Number.isNaN(feature.attributes[numberField.name])) {
                        feature.attributes[numberField.name] = null;
                    }
                });
            }

            // Convert GeoJSON geometry to an ESRI shaped geometry
            if (feature.geometry && feature.geometry.bbox) { delete (feature.geometry.bbox); }

            if (feature.geometry) {
                switch (feature.geometry.type) {
                    case 'Point':
                        feature.geometry.type = 'point';
                        feature.geometry.x = feature.geometry.coordinates[0];
                        feature.geometry.y = feature.geometry.coordinates[1];
                        break;
                    case 'LineString':
                    case 'MultiLineString':
                        feature.geometry.type = 'polyline';
                        feature.geometry.paths = feature.geometry.coordinates;
                        break;
                    case 'Polygon':
                        feature.geometry.type = 'polygon';
                        feature.geometry.rings = feature.geometry.coordinates;
                        break;
                    case 'MultiPoint':
                        feature.geometry.type = 'multipoint';
                        feature.geometry.points = feature.geometry.coordinates;
                        break;
                    case 'MultiPolygon':
                        // No direct support yet but this approach will atleast allow most/all to display...
                        // Flatten many polygons worth of rings into one polygon with many rings.
                        feature.geometry.type = 'polygon';
                        feature.geometry.rings = feature.geometry.coordinates.flat();
                        break;
                    default:
                        // Not supported
                        return [];
                }
                delete (feature.geometry.coordinates);
                return [feature];
            } else {
                return [];
            }
        });
        return source;
    }

}
